import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { ArrowLeft, Copy, Share2, Download, Clock, MessageSquareText, Check } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ConversationDetailPage = ({ user }) => {
  const { conversationId } = useParams();
  const [conversation, setConversation] = useState(null);
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    fetchConversation();
  }, [conversationId]);

  const fetchConversation = async () => {
    try {
      const token = localStorage.getItem('kaplie_token');
      const res = await axios.get(`${API}/conversations/${conversationId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setConversation(res.data);
    } catch (e) {
      toast.error('Failed to load conversation');
    } finally {
      setLoading(false);
    }
  };

  const getTranscriptText = () => {
    if (!conversation?.transcript) return '';
    return conversation.transcript
      .map(e => `${e.speaker}: ${e.text}`)
      .join('\n');
  };

  const handleCopy = async () => {
    const text = conversation.summary
      ? `Summary:\n${conversation.summary}\n\nFull Transcript:\n${getTranscriptText()}`
      : getTranscriptText();
    
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      toast.success('Transcript copied!');
      setTimeout(() => setCopied(false), 2000);
    } catch (e) {
      toast.error('Failed to copy');
    }
  };

  const handleShare = async () => {
    const text = getTranscriptText();
    if (navigator.share) {
      try {
        await navigator.share({
          title: `Kaplie Call with ${conversation.contact_name}`,
          text: conversation.summary || text.slice(0, 200),
        });
      } catch (e) {
        if (e.name !== 'AbortError') handleCopy();
      }
    } else {
      handleCopy();
    }
  };

  const handleDownload = () => {
    const header = `Kaplie Call Transcript\nWith: ${conversation.contact_name}\nDate: ${new Date(conversation.started_at).toLocaleString()}\n`;
    const summarySection = conversation.summary ? `\nSummary:\n${conversation.summary}\n` : '';
    const content = `${header}${summarySection}\n${'='.repeat(40)}\nTranscript:\n${'='.repeat(40)}\n\n${getTranscriptText()}`;
    
    const blob = new Blob([content], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `kaplie-call-${conversation.contact_name}-${new Date(conversation.started_at).toISOString().split('T')[0]}.txt`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success('Transcript downloaded');
  };

  const formatTime = (timestamp) => {
    try {
      return new Date(timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' });
    } catch {
      return '';
    }
  };

  const formatDuration = (seconds) => {
    if (!seconds) return '0s';
    const m = Math.floor(seconds / 60);
    const s = seconds % 60;
    return m > 0 ? `${m}m ${s}s` : `${s}s`;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-white/20 border-t-[#D4AF37] rounded-full animate-spin" />
      </div>
    );
  }

  if (!conversation) {
    return (
      <div className="min-h-screen bg-black text-white flex items-center justify-center">
        <div className="text-center">
          <p className="text-white/60 mb-4">Conversation not found</p>
          <Link to="/conversations" className="text-[#D4AF37] hover:text-[#D4AF37]/80">Go back</Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black text-white">
      <div className="fixed inset-0 bg-gradient-to-br from-[#D4AF37]/[0.04] via-black to-violet-900/[0.06] pointer-events-none" />

      {/* Header */}
      <nav className="relative z-10 border-b border-white/10 bg-black/50 backdrop-blur-xl sticky top-0">
        <div className="max-w-3xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Link to="/conversations">
                <button className="p-2 hover:bg-white/10 rounded-lg transition-colors" data-testid="back-to-conversations">
                  <ArrowLeft className="w-5 h-5 text-white/60" />
                </button>
              </Link>
              <div>
                <h1 className="font-semibold">{conversation.contact_name}</h1>
                <p className="text-xs text-white/40">
                  {new Date(conversation.started_at).toLocaleDateString([], { month: 'short', day: 'numeric', year: 'numeric' })}
                  {conversation.duration_seconds > 0 && ` - ${formatDuration(conversation.duration_seconds)}`}
                  {` - ${conversation.transcript?.length || 0} messages`}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-1">
              <button onClick={handleCopy} className="p-2.5 hover:bg-white/10 rounded-lg transition-colors" data-testid="copy-transcript">
                {copied ? <Check className="w-5 h-5 text-green-400" /> : <Copy className="w-5 h-5 text-white/60" />}
              </button>
              <button onClick={handleShare} className="p-2.5 hover:bg-white/10 rounded-lg transition-colors" data-testid="share-transcript">
                <Share2 className="w-5 h-5 text-white/60" />
              </button>
              <button onClick={handleDownload} className="p-2.5 hover:bg-white/10 rounded-lg transition-colors" data-testid="download-transcript">
                <Download className="w-5 h-5 text-white/60" />
              </button>
            </div>
          </div>
        </div>
      </nav>

      <main className="relative z-10 max-w-3xl mx-auto px-6 py-6">
        {/* AI Summary */}
        {conversation.summary && (
          <div className="mb-6 p-5 bg-gradient-to-r from-[#D4AF37]/10 to-[#C5A059]/5 rounded-2xl border border-[#D4AF37]/20" data-testid="conversation-summary">
            <div className="flex items-center gap-2 mb-3">
              <MessageSquareText className="w-4 h-4 text-[#D4AF37]" />
              <p className="text-sm font-semibold text-[#D4AF37]/80">AI Summary</p>
            </div>
            <p className="text-sm text-white/80 whitespace-pre-line leading-relaxed">{conversation.summary}</p>
          </div>
        )}

        {/* Transcript */}
        <div data-testid="transcript-list">
          <h2 className="text-sm font-semibold text-white/40 uppercase tracking-wider mb-4">Full Transcript</h2>
          <div className="space-y-3">
            {(conversation.transcript || []).map((entry, idx) => {
              const isMe = entry.speaker === 'You' || entry.speaker === user?.name;
              return (
                <div key={idx} className={`flex ${isMe ? 'justify-end' : 'justify-start'}`}>
                  <div className={`max-w-[80%] p-3 rounded-2xl ${
                    isMe
                      ? 'bg-[#D4AF37]/15 border border-[#D4AF37]/20 rounded-br-md'
                      : 'bg-white/5 border border-white/10 rounded-bl-md'
                  }`}>
                    <p className={`text-xs font-semibold mb-1 ${isMe ? 'text-[#D4AF37]/80' : 'text-violet-300'}`}>
                      {entry.speaker}
                    </p>
                    <p className="text-sm text-white/90">{entry.text}</p>
                    {entry.timestamp && (
                      <p className="text-[10px] text-white/30 mt-1 text-right">{formatTime(entry.timestamp)}</p>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </main>
    </div>
  );
};

export default ConversationDetailPage;
